import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login, requestPasswordReset, resetPassword } from '../api';
import { toast } from 'sonner';
import { LogIn, Snowflake, ArrowLeft, Eye, EyeOff, Mail, KeyRound } from 'lucide-react';

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1);
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      onLogin(res.data.user, res.data.token);
      toast.success('Erfolgreich angemeldet!');
      navigate(res.data.user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally { setLoading(false); }
  };

  const handleResetRequest = async () => {
    if (!resetEmail) { toast.error('Bitte E-Mail eingeben'); return; }
    setResetLoading(true);
    try {
      await requestPasswordReset(resetEmail);
      toast.success('Falls registriert, erhalten Sie eine E-Mail mit dem Reset-Code.');
      setResetStep(2);
    } catch (err) { toast.error('Fehler beim Zurücksetzen'); }
    finally { setResetLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!resetCode || !newPassword) { toast.error('Bitte alle Felder ausfüllen'); return; }
    if (newPassword.length < 6) { toast.error('Passwort muss mindestens 6 Zeichen haben'); return; }
    setResetLoading(true);
    try {
      await resetPassword(resetCode, newPassword);
      toast.success('Passwort erfolgreich geändert! Sie können sich jetzt anmelden.');
      setShowReset(false);
      setResetStep(1);
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler beim Zurücksetzen'); }
    finally { setResetLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#EEF3F8] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#7A9BB5] hover:text-[#2E3A46] text-sm mb-6 transition-colors">
            <ArrowLeft size={16} /> Zurück zur Startseite
          </Link>
          <div className="w-16 h-16 rounded-2xl bg-[#2E3A46] flex items-center justify-center mx-auto mb-4">
            <Snowflake size={28} className="text-[#7A9BB5]" />
          </div>
          <h1 className="font-heading text-3xl text-[#2E3A46] mb-2">Anmelden</h1>
          <p className="text-[#7A9BB5] text-sm">Birma von den Schneeblumen</p>
        </div>

        {!showReset ? (
          <div className="card-frost p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">E-Mail</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input"
                  className="w-full px-4 py-3 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] placeholder-[#C5D0DA] focus:border-[#7A9BB5] focus:outline-none"
                  placeholder="ihre@email.de" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Passwort</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input"
                    className="w-full px-4 py-3 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] placeholder-[#C5D0DA] focus:border-[#7A9BB5] focus:outline-none pr-12"
                    placeholder="Ihr Passwort" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C5D0DA] hover:text-[#7A9BB5] p-1">
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="login-submit-button"
                className="w-full btn-primary flex items-center justify-center gap-2 py-3 disabled:opacity-60">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <><LogIn size={18} />Anmelden</>}
              </button>
            </form>
            <button onClick={() => { setShowReset(true); setResetEmail(email); }} className="w-full text-center text-sm text-[#7A9BB5] hover:text-[#2E3A46] mt-4 py-2">
              Passwort vergessen?
            </button>
          </div>
        ) : (
          <div className="card-frost p-6 sm:p-8 animate-fade-in">
            <div className="text-center mb-6">
              <KeyRound size={28} className="text-[#7A9BB5] mx-auto mb-2" />
              <h2 className="font-heading text-xl text-[#2E3A46]">Passwort zurücksetzen</h2>
            </div>
            {resetStep === 1 ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">E-Mail Adresse</label>
                  <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none"
                    placeholder="ihre@email.de" />
                </div>
                <button onClick={handleResetRequest} disabled={resetLoading} className="w-full btn-primary flex items-center justify-center gap-2 py-3">
                  {resetLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <><Mail size={16} />Reset-Code senden</>}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-[#354551] text-center">Wir haben Ihnen einen Reset-Code per E-Mail gesendet.</p>
                <div>
                  <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Reset-Code</label>
                  <input type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none"
                    placeholder="Code aus der E-Mail" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Neues Passwort</label>
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none"
                    placeholder="Mind. 6 Zeichen" />
                </div>
                <button onClick={handleResetPassword} disabled={resetLoading} className="w-full btn-primary flex items-center justify-center gap-2 py-3">
                  {resetLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : 'Passwort ändern'}
                </button>
              </div>
            )}
            <button onClick={() => { setShowReset(false); setResetStep(1); }} className="w-full text-center text-sm text-[#7A9BB5] hover:text-[#2E3A46] mt-4 py-2">
              Zurück zur Anmeldung
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
